import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock, MapPin } from "lucide-react";
import { DIRECTION_LABEL, TRIP_STATUS, statusLabel, useLang } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CapacityMeter } from "@/components/CapacityMeter";
import { fmtDate } from "@/lib/format";

const STATUS_STYLE = {
  open: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  in_transit: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  arrived: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  closed: "bg-muted text-muted-foreground",
  completed: "bg-muted text-muted-foreground",
};

export const TripCard = ({ trip, index = 0 }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const full = trip.remaining_capacity_kg <= 0;
  const ordersClosed = trip.order_open === false;
  const bookable = !ordersClosed && !full;
  const btnLabel = full ? t("trip_full") : ordersClosed ? t("orders_closed") : t("book_this_trip");
  const badge = full
    ? { label: t("trip_full"), cls: "bg-destructive/15 text-destructive" }
    : ordersClosed
    ? { label: t("orders_closed"), cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" }
    : { label: t("trip_open"), cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" };

  return (
    <Card data-testid={`trip-card-${trip.id}`}
      className="p-6 flex flex-col gap-4 hover:shadow-lg hover:-translate-y-0.5 transition-all animate-fade-up"
      style={{ animationDelay: `${index * 80}ms` }}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Badge className={`${badge.cls} border-0 font-mono text-xs uppercase`}>
            {badge.label}
          </Badge>
          <h3 className="font-display font-bold text-lg mt-2 leading-tight">{trip.title}</h3>
        </div>
      </div>

      <div className="flex items-center gap-2 text-sm font-semibold text-primary">
        <span>{DIRECTION_LABEL[trip.direction]?.split(" → ")[0]}</span>
        <ArrowRight className="h-4 w-4" />
        <span>{DIRECTION_LABEL[trip.direction]?.split(" → ")[1]}</span>
      </div>

      <div className="space-y-1.5 text-sm text-muted-foreground">
        <p className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 shrink-0" />
          {t("deadline")}: <span className="font-medium text-foreground">{fmtDate(trip.receive_deadline, lang)}</span>
          <span className="text-xs">({trip.deadline_timezone === "birmingham" ? "BHX" : "WIB"})</span>
        </p>
        <p className="flex items-start gap-2">
          <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
          <span className="line-clamp-1">{trip.dropoff_address}</span>
        </p>
      </div>

      <CapacityMeter capacity={trip.capacity_kg} used={trip.used_capacity_kg} remaining={trip.remaining_capacity_kg} />

      {(ordersClosed || full) && (
        <p className="text-xs font-medium text-amber-600 dark:text-amber-400 -mb-2" data-testid={`trip-note-${trip.id}`}>
          {full ? t("trip_full") : t("orders_closed")}
        </p>
      )}
      <Button data-testid={`book-trip-btn-${trip.id}`} disabled={!bookable}
        onClick={() => navigate(`/book?trip=${trip.id}`)} className="mt-auto w-full">
        {btnLabel}
      </Button>
    </Card>
  );
};
